import { i18n } from '../i18n/i18n'

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { produce } from 'immer'
import { first, includes, remove } from 'lodash'
import workOrderService from '../services/workOrder.service'
import geolocationService from '../services/geolocation.service'
import printService from '../services/print.service'
import confirmService from '../services/confirm.service'
import itemPrintableService from '../services/itemPrintable.service'
import alertService from '../services/alert.service'
import palletApi from '../apis/pallet.api'
import workOrderApi, { WorkOrderData } from '../apis/workOrder.api'
import inventoryApi, {
  InventoryCreateMetadata, InventoryCreateRequest
} from '../apis/inventory.api'
import { MetaData } from '../apis/meta.api'
import metaAttributeApi from '../apis/addons/metaAttribute.api'
import { AddonName } from '../apis/addon.api'
import { ItemData } from '../apis/item.api'
import { ItemPrintableName } from '../apis/ItemPrintable.api'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import InputText from '../components/InputText'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import Divider from '../components/Divider'
import InventoryCreateMetadataFields from './InventoryCreateMetadataFields'
import CheckboxGroup from '../components/CheckboxGroup'
import { WatermarkHelper } from '../components/WatermarkHelper'

interface Props extends RouteComponentProps {
    onWorkOrderSelect: (workOrder: WorkOrderData | null) => void
}

interface State {
    workOrder: string
    workOrderId: number | null
    itemId: number | null
    categoryId: number | null
    quantity: string
    reference: string
    quantityOnCase: string
    labels: ItemPrintableName[]
    metadatas: InventoryCreateMetadata[]
    workOrders: WorkOrderData[]
    metas: MetaData[]
    quantityOnCaseMeta: MetaData | null
    latitude: number | null
    longitude: number | null
}

class InventoryCreateForm extends Component<Props, State> {
  resettableState: Omit<State, 'latitude' | 'longitude'> = {
    workOrder: '',
    workOrderId: null,
    itemId: null,
    categoryId: null,
    quantity: '',
    reference: '',
    quantityOnCase: '',
    labels: [],
    metadatas: [],
    workOrders: [],
    metas: [],
    quantityOnCaseMeta: null
  }

  constructor (props: Props) {
    super(props)
    this.state = {
      ...this.resettableState,
      latitude: null,
      longitude: null
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleWorkOrderSearch = this.handleWorkOrderSearch.bind(this)
    this.handleWorkOrderSelect = this.handleWorkOrderSelect.bind(this)
    this.handleWorkOrderBlur = this.handleWorkOrderBlur.bind(this)
    this.handleLabelsChange = this.handleLabelsChange.bind(this)
    this.handleMetaFieldChange = this.handleMetaFieldChange.bind(this)
    this.handleCreateClick = this.handleCreateClick.bind(this)
  }

  get workOrderOptions () {
    return workOrderService.searchOptions(this.state.workOrders)
  }

  get quantityOnCaseInput () {
    return this.state.quantityOnCaseMeta &&
            <InputText
                name="quantityOnCase"
                value={this.state.quantityOnCase}
                label={i18n('QPC')}
                onChange={this.handleInputChange}/>
  }

  listMetasByAddon (
    addon: AddonName, callback: (metas: MetaData[] | null) => void
  ) {
    const { itemId } = this.state
    if (!itemId) return
    metaAttributeApi.listMetasByAddon(addon, 'item', itemId, metas => {
      callback(metas)
    })
  }

  listCategoryMetasByAddon (
    addon: AddonName, callback: (metas: MetaData[] | null) => void
  ) {
    const { categoryId } = this.state
    if (!categoryId) return
    metaAttributeApi.listMetasByAddon(addon, 'category', categoryId, metas => {
      callback(metas)
    })
  }

  listMetas () {
    this.listMetasByAddon('extendInventoryCreateFormWhenItemEquals', metas => {
      if (!metas) return
      this.setState(state => {
        const updatedMetas = produce(state.metas, draft => [...draft, ...metas])
        return { metas: updatedMetas }
      })
    })
  }

  listCategoryMetas () {
    this.listCategoryMetasByAddon('extendInventoryCreateFormWhenCategoryNameEquals', metas => {
      if (!metas) return
      this.setState(state => {
        const updatedMetas = produce(state.metas, draft => [...draft, ...metas])
        return { metas: updatedMetas }
      })
    })
  }

  readQuantityOnCaseMeta (item: ItemData) {
    this.listMetasByAddon(
      'extendInventoryCreateFormWithQuantityOnCase',
      metas => {
        if (!metas) return
        const quantityOnCaseMeta = first(metas) || null
        const quantityOnCase = quantityOnCaseMeta ? String(item.qpc) : ''
        this.setState({ quantityOnCaseMeta })
        this.setState({ quantityOnCase })
      })
  }

  readActualWeightMeta () {
    this.listMetasByAddon(
      'extendInventoryCreateFormWithActualWeight',
      metas => {
        const meta = first(metas) || null
        if (!meta) return
        this.setState(state => {
          const updatedMetas = produce(state.metas, draft => [...draft, meta])
          return { metas: updatedMetas }
        })
      })
  }

  readGeolocation () {
    geolocationService.access(coordinate => {
      if (!coordinate) return
      const { latitude, longitude } = coordinate
      this.setState({ latitude, longitude })
    })
  }

  printLabels (inventoryId: number, palletId: number) {
    const { labels } = this.state
    const printCases = includes(labels, 'cases')
    const printPallet = includes(labels, 'pallet')
    if (printCases && printPallet) {
      inventoryApi.printLabels(inventoryId, markups => {
        if (!markups) return
        WatermarkHelper.printWithWM(markups)
        this.reset()
      })
    } else if (printCases) {
      inventoryApi.printCases(inventoryId, markups => {
        if (!markups) return
        WatermarkHelper.printWithWM(markups)
        this.reset()
      })
    } else if (printPallet) {
      palletApi.print(palletId, markups => {
        if (!markups) return
        WatermarkHelper.printWithWM(markups)
        this.reset()
      })
    } else {
      this.reset()
    }
  }

  reset () {
    this.setState(this.resettableState)
    this.props.onWorkOrderSelect(null)
  }

  handleInputChange (name: string, value: string) {
    if (name === 'workOrder') {
      this.setState({
        workOrderId: null,
        quantity: '',
        workOrder: value,
        metas: [],
        labels: []
      })
      this.props.onWorkOrderSelect(null)
    } else if (name === 'reference') this.setState({ reference: value })
    else if (name === 'quantityOnCase') this.setState({ quantityOnCase: value })
    else if (name === 'quantity') this.setState({ quantity: value })
  }

  handleWorkOrderSearch (name: string, code: string) {
    workOrderApi.searchByCode(code, workOrders => {
      if (!workOrders) return
      this.setState({ workOrders })
    })
  }

  handleWorkOrderSelect (name: string, option: SearchPickerOption) {
    const workOrderId = option.id as number
    const workOrder = workOrderService.find(this.state.workOrders, workOrderId)
    if (!workOrder) return
    const { item } = workOrder
    if (!item) return
    this.props.onWorkOrderSelect(workOrder)
    const labels = itemPrintableService.printableNames(item.printables)
    const state = {
      workOrders: [],
      workOrderId,
      itemId: item.id,
      categoryId: item.categoryId,
      quantity: String(item.cpp),
      quantityOnCase: '',
      labels
    }
    this.setState(state, () => {
      this.listMetas()
      this.listCategoryMetas()
      this.readQuantityOnCaseMeta(item)
      this.readActualWeightMeta()
    })
  }

  handleWorkOrderBlur (name: string) {
    this.setState({ workOrders: [] })
  }

  handleLabelsChange (labels: ItemPrintableName[]) {
    this.setState({ labels })
  }

  handleMetaFieldChange (metaId: number, value: string) {
    const metadatas = produce(this.state.metadatas, metadatas => {
      const metadata: InventoryCreateMetadata = { metaId, value }
      remove(metadatas, metadata => (metadata.metaId === metaId))
      metadatas.push(metadata)
    })
    this.setState({ metadatas })
  }

  handleCreateConfirm () {
    const { workOrderId, quantity, reference } = this.state
    const { metadatas, quantityOnCase, latitude, longitude } = this.state
    const payload: InventoryCreateRequest = {}
    if (workOrderId) payload.workOrder = workOrderId
    if (quantity) payload.quantity = Number(quantity)
    if (reference) payload.reference = reference
    if (metadatas) payload.metadatas = metadatas
    if (quantityOnCase) payload.qpc = Number(quantityOnCase)
    if (latitude) payload.latitude = latitude
    if (longitude) payload.longitude = longitude
    inventoryApi.create(payload, inventory => {
      if (!inventory || !inventory.pallets) return
      alertService.success('Successfully created inventory')
      const pallet = inventory.pallets[0]
      this.printLabels(inventory.id, pallet.id)
    })
  }

  handleCreateClick () {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please review inventory details above and then confirm'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.handleCreateConfirm()
    }
    confirmService.push(confirm)
  }

  componentDidMount () {
    this.readGeolocation()
  }

  render () {
    return (
            <Card>
                <PageHeading
                    text={i18n('Create Inventory')}
                    doc="inventories-create-inventory"/>
                <Row column={3}>
                    <SearchPicker
                        name="workOrder"
                        value={this.state.workOrder}
                        options={this.workOrderOptions}
                        label={i18n('Work order')}
                        onSearch={this.handleWorkOrderSearch}
                        onChange={this.handleInputChange}
                        onSelect={this.handleWorkOrderSelect}
                        onBlur={this.handleWorkOrderBlur}/>
                    <InputText
                        name="quantity"
                        value={this.state.quantity}
                        label={i18n('Quantity')}
                        onChange={this.handleInputChange}/>
                    <InputText
                        name="reference"
                        value={this.state.reference}
                        label={i18n('Reference')}
                        onChange={this.handleInputChange}/>
                </Row>
                <Row column={1}>
                    <CheckboxGroup
                        checks={itemPrintableService.labelChecks}
                        checkeds={this.state.labels}
                        label={i18n('Labels')}
                        onChange={this.handleLabelsChange}/>
                </Row>
                <Divider/>
                <Row column={3}>
                    {this.quantityOnCaseInput}
                </Row>
                <InventoryCreateMetadataFields
                    metas={this.state.metas}
                    onChange={this.handleMetaFieldChange}/>
                <CardButton
                    proceedText={i18n('Create')}
                    onProceedClick={this.handleCreateClick}/>
            </Card>
    )
  }
}

export default withRouter(InventoryCreateForm)
